/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSlice, type EntityState, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { systemSettingsService } from "~/services/system-settings-service";
import type { System } from "~/types/systemSetting";

export const systemSettingsAdapter = createEntityAdapter<System>({
  selectId: (system) => system.id,
});

type SettingsState = {
  system: EntityState<System>;
  isLoading: boolean;
  errorMessage: string;
  selectedRowIndex: number;
};

const initialState: SettingsState = {
  system: systemSettingsAdapter.getInitialState(),
  isLoading: false,
  errorMessage: "",
  selectedRowIndex: -1,
};

export const fetchSystemSettings = createAsyncThunk("settings/fetchSystemSettings", async (_, thunkApi) => {
  try {
    return await systemSettingsService.getAllSystemSettings();
  } catch {
    return thunkApi.rejectWithValue("Unable to fetch system settings. Please try again.");
  }
});

export const updateSystemSetting = createAsyncThunk(
  "settings/updateSystemSetting",
  async (setting: System, thunkApi) => {
    try {
      await systemSettingsService.updateSystemSetting(setting);
      return setting;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSelectedRowIndex: (state, action: PayloadAction<number>) => {
      state.selectedRowIndex = action.payload;
    },
    clearSettingsError: (state) => {
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.pending, (state) => {
        if (state.system.ids.length === 0) {
          state.isLoading = true;
        }
        state.errorMessage = "";
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        const selectedSettingId =
          state.selectedRowIndex >= 0
            ? systemSettingsAdapter.getSelectors().selectAll(state.system)[state.selectedRowIndex]?.id
            : undefined;

        systemSettingsAdapter.setAll(state.system, action.payload);
        state.selectedRowIndex = selectedSettingId
          ? action.payload.findIndex((setting) => setting.id === selectedSettingId)
          : -1;
        state.isLoading = false;
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        systemSettingsAdapter.removeAll(state.system);
        state.selectedRowIndex = -1;
        state.isLoading = false;
        state.errorMessage =
          (action.payload as string) || action.error.message || "Unable to fetch system settings. Please try again.";
      })
      .addCase(updateSystemSetting.fulfilled, (state, action) => {
        systemSettingsAdapter.upsertOne(state.system, action.payload);
      });
  },
});

export const settingsActions = settingsSlice.actions;
export default settingsSlice.reducer;

const settingsSystemEntitySelectors = systemSettingsAdapter.getSelectors((state: RootState) => state.settings.system);

export const settingsSystem = settingsSystemEntitySelectors.selectAll;
export const settingsSystemEntities = settingsSystemEntitySelectors.selectEntities;
export const settingsLoading = (state: RootState) => state.settings.isLoading;
export const settingsErrorMessage = (state: RootState) => state.settings.errorMessage;
export const settingsSelectedRowIndex = (state: RootState) => state.settings.selectedRowIndex;